// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import BloqueFicha, { TemaFicha } from "../components/funcionales/BloqueFicha";
import BloqueMultasPersona from "../components/funcionales/BloqueMultasPersona";
import ChipUsuario from "../components/funcionales/ChipUsuario";
import DomicilioBloque from "../components/funcionales/Domicilio";
import DondeEsta from "../components/funcionales/DondeEsta";
import HerramientasDePersona from "../components/funcionales/HerramientasDePersona";
import Icono from "../components/funcionales/IconoFicha";
import TractorAsignado from "../components/funcionales/TractorAsignado";
import DosAltas, {
  avisaSinFicha,
  avisaSinNomina,
} from "../components/funcionales/DosAltas";
import Pin from "../components/funcionales/Pin";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import Telefono from "../components/funcionales/Telefono";
import { documentacionDe } from "../services/documentacion";
import { domicilioDe } from "../services/domicilio";
import {
  cargadaPor,
  estadoDeLicencia,
  licenciasDe,
  rotuloDeEstado,
} from "../services/licencias";
import { novedadesDe } from "../services/rrhhNovedades";
import { lineaDeTiempo } from "../shared/rrhhNovedades";
import { cruceDe, tacografoDe, tienePin } from "../services/choferCruce";
import { nominaDe, periodosDe } from "../services/rrhh";
import { useRealtime } from "../services/store";
import {
  etiquetaCorta,
  etiquetaConCuit,
  claseDeEmpresa,
} from "../shared/empresa";
import {
  DIAS_AVISO,
  diasHasta,
  estaDeBaja,
  evaluar,
} from "../shared/documentacion";
import { nombreDePersona } from "../shared/personas";
import { antiguedad } from "../shared/rrhh";
import { estadoDeUnidad, textoDeEstado } from "../services/cols/estado";

// ----------------------------------------------------------------------- estilos
import "./css/fichas-pantalla.css";
import { hoyAr } from "../shared/fechaAr";

/* La ficha de una persona: lo que se sabe de un chofer, en un lugar.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────
 *
 * Es el gemelo de la ficha de la unidad y nace del mismo hueco: los datos ya
 * estaban todos adentro del hub —el último cruce del portón, el teléfono
 * cruzado entre el padrón y la planilla, los vencimientos— y no había ninguna
 * pantalla que los juntara. El buscador mandaba a `/padron/personas?buscar=`,
 * o sea a una tabla filtrada: un resultado que obliga a leer una fila de
 * veinte columnas para contestar «¿dónde anda?».
 *
 * ── NO CARGA NADA ────────────────────────────────────────────────────────
 *
 * De lectura, igual que la de la unidad. El padrón se corrige con el lápiz en
 * su pantalla y los vencimientos en Documentos. Dos lugares para cargar lo
 * mismo obligan a adivinar en cuál.
 *
 * ── ÉSTA SÍ PIDE PERMISO, Y ES LA DIFERENCIA CON LA DE LA UNIDAD ─────────
 *
 * La ficha de un camión no lleva permiso porque el mapa ya muestra lo mismo
 * sin pedir ninguno. Acá no hay equivalente: **el DNI, el teléfono y el
 * domicilio laboral de una persona no están abiertos en ningún lado del hub**,
 * y la pantalla de donde salen —el padrón de gente— pide `ver.gente`. Pedir
 * menos que el padrón sería abrir por la ventana lo que la puerta tiene con
 * llave.
 *
 * Adentro, cada bloque cuelga del permiso de SU pantalla, igual que la unidad.
 */

/* Los papeles que se miran, y con qué criterio.
 *
 * **MOSTRAR NO ES AVISAR**, y acá se ven las dos cosas a la vez: el
 * psicofísico se MUESTRA —hace falta para renovar el registro— y NO cuenta
 * como faltante, porque hay 142 vencidos de 153 y con eso arriba la pantalla
 * se deja de mirar. `evaluar()` ya aplica ese criterio y es el mismo que usan
 * el mail de los lunes y el portón; acá sólo se dibuja.
 *
 * Las cargas peligrosas salen sólo para los marcados: pedírselas a los ~200
 * que no las hacen convierte 2 vencimientos reales en 53.
 */
const PAPELES = [
  ["registro_vence", "Registro", true],
  ["peligrosas_vence", "Cargas peligrosas", "solo_marcados"],
  ["psicofisico_vence", "Psicofísico", "muestra"],
];

const fecha = (f) =>
  f
    ? new Date(String(f).slice(0, 10) + "T12:00:00").toLocaleDateString("es-AR")
    : null;

/* Cada papel tiene CUATRO estados y no dos. «Sin fecha» es el más accionable
 * de todos y el que más fácil se confunde con «al día»: dibujarlos igual
 * declara en regla a alguien que nadie miró. */
/* La antigüedad sale de `shared/rrhh` y ya no se calcula acá: la misma cuenta
 * la hace el listado de la nómina, y escrita en los dos lugares el día que se
 * corrija el redondeo en uno la misma persona tendría dos antigüedades según
 * desde dónde se la mire. */

function tono(dias) {
  if (dias === null) return { clase: "gris", texto: "Sin fecha" };
  if (dias < 0)
    return { clase: "mal", texto: `Vencido hace ${Math.abs(dias)} días` };
  if (dias <= DIAS_AVISO)
    return { clase: "ojo", texto: `Vence en ${dias} días` };
  return { clase: "ok", texto: "Al día" };
}

const Persona = () => {
  const { dni } = useParams();
  const { permisos = null } = useOutletContext() || {};

  const puede = useCallback(
    (p) => permisos === null || permisos.includes(p),
    [permisos],
  );

  const limpio = String(dni ?? "").replace(/\D/g, "");
  const personas = useRealtime("personas");
  const fila = useMemo(
    () =>
      limpio
        ? (personas || []).find(
            (p) => String(p.dni ?? "").replace(/\D/g, "") === limpio,
          )
        : null,
    [personas, limpio],
  );

  /* La documentación es el único bloque que sale a la red desde acá: el
   * teléfono y el último cruce los piden sus propios componentes, que ya
   * existían y ya saben no frenar la pantalla si fallan. */
  const [docs, setDocs] = useState(undefined); // undefined = cargando
  const verDocs = puede("ver.documentos");

  useEffect(() => {
    if (!limpio || !verDocs) return undefined;
    let vigente = true;
    setDocs(undefined);
    try {
      documentacionDe(limpio)
        .then((d) => vigente && setDocs(d))
        .catch(() => vigente && setDocs(false)); // false = no se pudo
    } catch {
      setDocs(false);
    }
    return () => {
      vigente = false;
    };
  }, [limpio, verDocs]);

  /* RRHH va aparte del resto y sale de dos lados distintos: la nómina de
   * sueldos (`nomina`) y el historial que vive adentro de `personas.periodos`.
   *
   * Las dos consultas se piden juntas y se guardan aparte: si la nómina no
   * tiene a esta persona —son 292 de 2.476— el historial igual se muestra.
   * Encadenarlas haría que no estar en la nómina esconda diez años de
   * historia. */
  const verRrhh = puede("ver.rrhh");
  const [nomina, setNomina] = useState(undefined);
  const [periodos, setPeriodos] = useState(undefined);
  const [licencias, setLicencias] = useState(undefined);
  const [novedades, setNovedades] = useState(undefined);

  /* ── Las tres que faltaban ──────────────────────────────────────────────
   *
   * VAN EN SU PROPIO EFECTO Y NO SE ENCADENAN AL DE RRHH, por dos motivos.
   * Uno: el cruce de padrones NO lleva `ver.rrhh` —saber que a alguien le
   * falta media ficha es lo que evita que quien mira sus vencimientos crea
   * que están sin cargar, y ése es cualquiera que abra la pantalla—. Dos: si
   * la nómina no contesta, el tacógrafo y la tarjeta tienen que seguir
   * viéndose. Es la misma razón por la que `novedades` está suelta de
   * `licencias`.
   *
   * Los tres servicios devuelven `{dato, error}` y nunca tiran, así que acá
   * el estado es el de siempre: `undefined` cargando, `false` no se pudo. */
  const [cruce, setCruce] = useState({ estado: "cargando", dato: null });
  const [pin, setPin] = useState({ dato: false });
  const [taco, setTaco] = useState({ dato: null });

  useEffect(() => {
    if (!limpio) return undefined;
    let sigue = true;
    setCruce({ estado: "cargando", dato: null });
    setPin({ dato: false });
    setTaco({ dato: null });
    cruceDe(limpio).then((r) => {
      if (!sigue) return;
      setCruce(
        r.error
          ? { estado: "error", dato: null }
          : { estado: r.dato ? "listo" : "vacio", dato: r.dato },
      );
    });
    // El PIN y el tacógrafo dibujan su bloque SÓLO si hay algo. Un «no tiene
    // tarjeta» en las 2.100 personas que no manejan es ruido, y en el caso del
    // PIN además sería mentira: sin `ver.pin_ypf` la RLS devuelve vacío igual
    // que si no existiera, así que un cero no se puede afirmar.
    tienePin(limpio).then((r) => sigue && setPin(r));
    tacografoDe(limpio).then((r) => sigue && setTaco(r));
    return () => {
      sigue = false;
    };
  }, [limpio]);

  /* ── El domicilio ───────────────────────────────────────────────────────
   *
   * EFECTO PROPIO Y COLGADO DE SU PERMISO, que no es `ver.rrhh` ni
   * `ver.gente` sino uno solo suyo, `ver.domicilio_chofer`, que arranca sin
   * nadie: «nadie puede verlo… se lo agrego yo a mano a los que lo
   * necesitan». Sin la llave ni se consulta — la RLS igual devolvería vacío,
   * pero una consulta que nunca puede traer nada es una que el día de mañana
   * alguien mira y cree que el dato está roto.
   *
   * Y por eso mismo el vacío de acá SÍ se puede leer: quien llega tiene el
   * permiso, así que «no vino nada» es «no está cargado» y no «no podés». */
  const verDomicilio = puede("ver.domicilio_chofer");
  const [domicilio, setDomicilio] = useState({ estado: "cargando", dato: null });

  useEffect(() => {
    if (!limpio || !verDomicilio) return undefined;
    let sigue = true;
    setDomicilio({ estado: "cargando", dato: null });
    domicilioDe(limpio).then((r) => {
      if (!sigue) return;
      setDomicilio(
        r.error ? { estado: "error", dato: null } : { estado: "listo", dato: r.dato },
      );
    });
    return () => {
      sigue = false;
    };
  }, [limpio, verDomicilio]);

  useEffect(() => {
    if (!limpio || !verRrhh) return undefined;
    let sigue = true;
    setNomina(undefined);
    setPeriodos(undefined);
    setLicencias(undefined);
    setNovedades(undefined);
    nominaDe(limpio)
      .then((d) => sigue && setNomina(d))
      .catch(() => sigue && setNomina(false)); // false = no se pudo
    periodosDe(limpio)
      .then((d) => sigue && setPeriodos(d))
      .catch(() => sigue && setPeriodos(false));
    licenciasDe(limpio)
      .then((d) => sigue && setLicencias(d))
      .catch(() => sigue && setLicencias(false));
    // Aparte de licencias y no encadenada: si `rrhh_novedades` falla —o
    // todavía no tiene nada— las licencias tienen que seguir viéndose.
    novedadesDe(limpio)
      .then((d) => sigue && setNovedades(d))
      .catch(() => sigue && setNovedades(false));
    return () => {
      sigue = false;
    };
  }, [limpio, verRrhh]);

  if (!limpio) {
    return (
      <div className="panel-aviso">
        <h3>Esa persona no existe</h3>
        <p>La dirección tiene que llevar un DNI.</p>
      </div>
    );
  }

  /* El permiso se chequea acá y no bloque por bloque: sin `ver.gente` no hay
   * ficha, del mismo modo que no hay padrón. */
  if (!puede("ver.gente")) {
    return (
      <PuertaCerrada />
    );
  }

  const nombre = nombreDePersona(fila);
  /* Las cinco formas que trae `estado` desde Firestore —"1", "true", "0",
   * "false", vacío— las traduce `estadoDeUnidad`, la misma función que usa la
   * tabla. Leídas a mano acá, la ficha diría «Activo» de alguien que el padrón
   * da de baja: `estado || …` es verdadero para el string "false". */
  const estado = fila ? estadoDeUnidad(fila.estado) : null;
  const r = docs && docs !== true ? evaluar(docs) : null;

  /* ── ¿Es chofer? ──────────────────────────────────────────────────────────
   *
   * Igna, 02/10: *«Si no es chofer la parte de "Tractor asignado, donde se lo
   * vio, documentacion, infracciones, multas" no debería existir jamás»*. En
   * la ficha de una administrativa esos bloques decían cinco veces «no hay»
   * sobre preguntas que no se le hacen a ella.
   *
   * Chofer es cualquiera de las tres cosas, porque cada fuente ve una parte:
   * la nómina lo marca (`es_chofer`), Marcos le lleva la ficha, o la planilla
   * de documentación trajo su fila. Una sola alcanza — los 60 que están con
   * Marcos y no en la nómina son choferes aunque RRHH no lo diga.
   *
   * MIENTRAS EL CRUCE NO LLEGÓ NO SE SABE, y esos bloques todavía no se
   * dibujan: aparecer y desaparecer a los 200 ms se lee como que la pantalla
   * se corrigió sola. */
  const esChofer =
    cruce.dato?.enNomina?.es_chofer === true ||
    Boolean(cruce.dato?.enDocumentos) ||
    (docs !== null && typeof docs === "object");

  const enPrueba = Boolean(nomina) && /prueba/i.test(nomina.contrato || "");
  /* De baja, los papeles no avisan: es la misma regla que Documentos y el
   * mail de los lunes, y avisar acá un registro vencido de alguien que ya no
   * maneja mandaría a llamarlo. Se avisa la baja, que es lo que hay que saber. */
  const avisosDocs = !docs || !r
    ? []
    : estaDeBaja(docs)
      ? ["está de baja: sus vencimientos no cuentan en los avisos"]
      : [
        ...r.faltan,
        ...(r.prorroga
          ? [
              `registro vencido hace ${r.prorroga.vencidoHace} días, en prórroga hasta el ${fecha(r.prorroga.hasta)}`,
            ]
          : []),
        ...r.avisa,
      ];

  /* ── Lo que avisa, arriba de todo ─────────────────────────────────────────
   *
   * Con los bloques ordenados por tema, un aviso queda donde le toca —el de
   * las dos altas, en Papeles, abajo de todo— y el que abre la ficha para
   * saber si hay algo que hacer tendría que recorrerla entera. Así que lo que
   * avisa SE NOMBRA ARRIBA, en un renglón, diciendo en qué bloque está; y el
   * bloque además queda pintado en su lugar, para encontrarlo.
   *
   * SÓLO LO QUE LA PANTALLA YA CALCULA: los papeles con el mismo `evaluar()`
   * del mail de los lunes (el psicofísico no avisa, y eso no cambia acá), las
   * dos altas con la misma regla que dibuja `DosAltas`, y el período de prueba
   * con la misma pastilla ámbar de RRHH. Ninguna consulta nueva.
   *
   * CADA AVISO PIDE LA LLAVE DE SU BLOQUE: un renglón que dice «no está en la
   * nómina» arriba de todo es la nómina, aunque el bloque esté más abajo. */
  const alertas = [];
  if (verRrhh && esChofer && cruce.dato) {
    if (avisaSinFicha(cruce.dato))
      alertas.push({
        clave: "altas-ficha",
        bloque: "Las dos altas",
        texto:
          "está en la nómina y no tiene ficha de documentación. No sabemos si tiene el registro al día.",
      });
    if (avisaSinNomina(cruce.dato))
      alertas.push({
        clave: "altas-nomina",
        bloque: "Las dos altas",
        texto: "tiene ficha de documentación y no está en la nómina.",
      });
  }
  if (verDocs && esChofer && avisosDocs.length)
    alertas.push({
      clave: "docs",
      bloque: "Documentación",
      texto: avisosDocs.join(" · ") + ".",
    });
  if (verRrhh && enPrueba)
    alertas.push({
      clave: "prueba",
      bloque: "RRHH",
      texto: "está en período de prueba. Vence, y hay que decidir antes.",
    });
  const alertaAltas = alertas.some((a) => a.bloque === "Las dos altas");

  return (
    <div className="pf">
      <header className="pf-cabeza">
        <div className="pf-quien">
          <h2>{nombre || `DNI ${limpio}`}</h2>
          <p className="tk-meta">
            DNI {limpio}
            {fila?.legajo ? ` · legajo ${fila.legajo}` : ""}
            {fila?.puesto ? ` · ${fila.puesto}` : ""}
          </p>
        </div>
        {/* El estado del padrón sale de `estadoDe`, la misma traducción que usa
            la tabla: son cinco formas heredadas de Firestore —"1", "true", "0",
            "false", vacío— y leerlas a mano acá haría que la ficha diga
            «Activo» de alguien que la tabla da de baja. */}
        {estado && (
          <span className={"pill " + (estado === "activo" ? "ok" : "gris")}>
            {textoDeEstado(fila.estado)}
          </span>
        )}
      </header>

      {/* Sin fila del padrón la ficha NO se corta: portería y la planilla van
          por DNI y esos bloques siguen. Son 13 los DNI que están en la
          planilla de documentación y no en el padrón. */}
      {!fila && (
        <p className="pf-nopude">
          Este DNI no está en el padrón de personas. Lo que se muestra abajo
          sale de portería y de la planilla de documentación, que van por DNI.
        </p>
      )}

      {alertas.length > 0 && (
        <ul className="pf-alertas" aria-label="Para mirar">
          {alertas.map((a) => (
            <li key={a.clave} className="pf-alerta">
              <Icono nombre="alerta" />
              <span>
                <b>{a.bloque}:</b> {a.texto}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* ── Cuatro temas y no una grilla de dieciséis (Igna, 02/10) ──────────
          Eligió la «opción E» de las cinco que se le mostraron: los bloques
          agrupados por la pregunta que contestan —Hoy, Contacto, Empleo,
          Papeles—, cada tema con su color y su ícono, y adentro SÓLO LO QUE
          TIENE ALGO. Antes eran dieciséis recuadros iguales en el orden en que
          se fueron agregando, y para saber cuáles decían algo había que leerlos
          todos.

          Cada bloque lleva `ocultarSiVacio`: el vacío no se dibuja, y un tema
          sin ningún bloque a la vista tampoco (lo resuelve `TemaFicha`).
          «Consultando…» y «no se pudo consultar» SÍ se ven: no son un vacío. */}
      <div className="pf-temas">
        {/* ── Hoy: dónde está y con qué trabaja ───────────────────────────── */}
        <TemaFicha id="hoy" titulo="Hoy" icono={<Icono nombre="ojo" />}>
          {/* ── Dónde se lo vio ────────────────────────────────────────────
              Sólo para choferes: el portón cuenta camiones, y «no figura
              cruzando» en la ficha de alguien de oficina no es un dato. */}
          {puede("ver.porteria") && esChofer && (
            <BloqueFicha
              titulo="Dónde se lo vio"
              estado="listo"
              ocultarSiVacio
              comoLlegar={
                <Link className="rto-link" to="/porteria">
                  Ir a portería
                </Link>
              }
            >
              {/* `DondeEsta` trae su propia regla de qué se puede afirmar: tres
                  de sus cuatro estados son «no se puede saber», y el interno
                  sirve aunque no haya lugar — con ese número se la busca en el
                  mapa en vivo. Sin ningún cruce, le avisa al bloque que está
                  vacío y el bloque no se dibuja. */}
              <DondeEsta dni={limpio} />
            </BloqueFicha>
          )}

          {/* ── El tractor asignado (pedido de Nico) ────────────────────────
              «Chofer XXX > tractor YYY», dicho por una persona y editable acá.

              VA JUNTO A «DÓNDE SE LO VIO» Y NO EN RRHH: las dos contestan la
              misma pregunta de tráfico —con qué camión anda éste— y separadas
              obligarían a mirar dos partes de la ficha para armar una
              respuesta.

              SÓLO PARA CHOFERES (Igna, 02/10), y sin asignación ni propuesta
              de portería no se dibuja — salvo para quien puede asignar, que
              ahí tiene el botón (lo explica `TractorAsignado`).

              NO LLEVA PERMISO PROPIO. La ficha entera ya pide `ver.gente` para
              abrirse, y la RLS de `chofer_tractor` deja leer con `ver.gente` o
              con `ver.flota`: el que llegó hasta acá puede ver esta fila. Pedir
              además `ver.flota` dejaría a los 16 de RRHH mirando un hueco donde
              el resto ve el camión — y un hueco se lee como un dato que falta,
              no como un permiso que falta. Cargar sí pide `cargar.flota`: de
              quién es un camión lo decide Tráfico. */}
          {esChofer && (
            <BloqueFicha
              titulo="Tractor asignado"
              estado="listo"
              ocultarSiVacio
              comoLlegar={
                <Link className="rto-link" to="/padron/tractores">
                  Ir al padrón
                </Link>
              }
            >
              <TractorAsignado dni={limpio} puedeCargar={puede("cargar.flota")} />
            </BloqueFicha>
          )}

          {/* ── Lo que se le prestó del tablero (pedido de Igna, 15/09) ─────
              «Debería haber una tarjeta que diga cuándo se le prestó algo. Y
              lo quiero histórico. Cuando lo devuelva que quede registro de
              cuándo lo devolvió y cuánto tiempo lo tuvo.»

              NO HUBO QUE GUARDAR NADA NUEVO: `taller_prestamo` escribe el
              retiro, la devolución y quién hizo cada cosa desde el primer día.
              El historial existía y no se miraba desde ningún lado.

              VA EN «HOY», al lado de «dónde se lo vio» y del tractor: las tres
              contestan qué tiene encima esta persona hoy, que es lo que se mira
              antes de mandarla a la ruta o de reclamarle algo. A diferencia de
              esas dos NO es sólo de choferes: a un mecánico también se le
              presta. Si nunca se le prestó nada, no se dibuja.

              NO LLEVA PERMISO PROPIO, por lo mismo que el tractor: la ficha ya
              pide `ver.gente` para abrirse y la RLS de `taller_prestamo` deja
              leer a cualquiera con sesión. Pedir además `ver.taller` dejaría a
              RRHH mirando un hueco donde el resto ve el dato — y un hueco se
              lee como un dato que falta, no como un permiso que falta. */}
          <BloqueFicha
            titulo="Herramientas"
            estado="listo"
            ocultarSiVacio
            comoLlegar={
              <Link className="rto-link"
                    to={`/taller/herramientas/caja/${encodeURIComponent(limpio)}`}>
                Ver su caja
              </Link>
            }
          >
            <HerramientasDePersona dni={limpio} />
          </BloqueFicha>

          {/* ── Tarjeta YPF ────────────────────────────────────────────────
              SI TIENE, y el número recién al apretar. Es una credencial de
              pago con su propia RLS: traerla con el resto la dejaría en la
              memoria del navegador de todo el que abra una ficha, sin que nadie
              la haya pedido. Va en «Hoy» porque es con qué carga combustible
              esta semana, no un papel. */}
          {pin.dato && (
            <BloqueFicha titulo="Tarjeta YPF" estado="listo" ocultarSiVacio>
              <Pin dni={limpio} />
            </BloqueFicha>
          )}

          {/* ── Tacógrafo ──────────────────────────────────────────────── */}
          {taco.dato && (
            <BloqueFicha titulo="Tacógrafo" estado="listo" ocultarSiVacio>
              <p className="mono">{taco.dato.tarjeta}</p>
              {taco.dato.confirmado_nombre && (
                <p className="tenue pf-chico">
                  Vinculada por {taco.dato.confirmado_nombre}.
                </p>
              )}
            </BloqueFicha>
          )}
        </TemaFicha>

        {/* ── Contacto: cómo ubicarlo ─────────────────────────────────────── */}
        <TemaFicha id="contacto" titulo="Contacto" icono={<Icono nombre="tel" />}>
          {/* ── Teléfono ─────────────────────────────────────────────────── */}
          <BloqueFicha titulo="Teléfono" estado="listo" ocultarSiVacio>
            {/* Cruza el padrón con la planilla y muestra la discrepancia en vez
                de resolverla. 176 personas ganan un número acá sin que nadie
                cargue nada. Sin número en ninguna de las dos, el bloque no se
                dibuja. */}
            <Telefono dni={limpio} delPadron={fila?.telefono} />
          </BloqueFicha>

          {/* ── Dónde vive ─────────────────────────────────────────────────
              AL LADO DEL TELÉFONO: el que tiene esta llave la pidió para poder
              ubicar a alguien, y llamarlo y saber dónde vive son la misma
              gestión.

              SIN EL PERMISO NO SE DIBUJA NI EL TÍTULO. Antes era lo contrario
              de lo que hacían los demás bloques de esta ficha; desde que lo
              vacío no aparece (Igna, 02/10) es lo mismo que hacen todos, pero
              acá la razón es otra y sigue valiendo sola: un «no tenés permiso
              para ver el domicilio» ya cuenta que el dato está cargado y que
              hay gente que lo ve, y son 288 domicilios de empleados. */}
          {verDomicilio && (
            <BloqueFicha titulo="Dónde vive" estado={domicilio.estado} ocultarSiVacio>
              <DomicilioBloque fila={domicilio.dato} />
            </BloqueFicha>
          )}
        </TemaFicha>

        {/* ── Empleo: RRHH, licencias y novedades ─────────────────────────── */}
        <TemaFicha id="empleo" titulo="Empleo" icono={<Icono nombre="persona" />}>
          {/* ── RRHH ───────────────────────────────────────────────────────
              Cuelga de `ver.rrhh` y no de `ver.gente`, que lo tienen quince
              personas. Lo de acá —antigüedad reconocida, obra social y, cuando
              lleguen, los motivos de egreso— no está en ninguna otra pantalla
              del hub. La base opina lo mismo: sin la llave, `nomina` devuelve
              cero filas. */}
          {verRrhh && (
            <BloqueFicha
              titulo="RRHH"
              ocultarSiVacio
              alerta={enPrueba}
              estado={
                nomina === undefined
                  ? "cargando"
                  : nomina === false
                    ? "error"
                    : "listo"
              }
              vacio={
                nomina === null
                  ? "No está en la nómina de sueldos de este mes. Son 292 personas sobre 2.476: no estar acá quiere decir que hoy no cobra en ninguna de las cuatro empresas."
                  : null
              }
            >
              {nomina && (
                <ul className="pf-papeles">
                  <li>
                    <span className="et">Empresa</span>
                    {/* Con el CUIT, como en la ficha del camión: es una
                        ficha y hay lugar. Pedido de Nico (28/08/2026). */}
                    <span className={"pill " + claseDeEmpresa(nomina.empresa)}>
                      {etiquetaConCuit(nomina.empresa)}
                    </span>
                    <span className="tk-item-meta">
                      {nomina.destino || "sin base"}
                    </span>
                  </li>
                  <li>
                    <span className="et">Antigüedad</span>
                    <span className="dato">
                      {antiguedad(nomina.ingreso_computable) || "—"}
                    </span>
                    <span className="tk-item-meta">
                      desde {fecha(nomina.ingreso_computable) || "—"}
                    </span>
                  </li>
                  {/* SÓLO SE MUESTRA CUANDO DIFIEREN, y entonces importa:
                      quiere decir que a esta persona se le reconoce antigüedad
                      de antes —volvió, o pasó de una empresa del grupo a
                      otra—. Hoy pasa en una de 292; mostrar las dos fechas
                      siempre haría que esa una no se note. */}
                  {nomina.ingreso_real !== nomina.ingreso_computable && (
                    <li>
                      <span className="et">Ingreso real</span>
                      <span className="dato">
                        {fecha(nomina.ingreso_real) || "—"}
                      </span>
                      <span className="tk-item-meta">
                        se le reconoce antigüedad anterior
                      </span>
                    </li>
                  )}
                  <li>
                    <span className="et">Tarea</span>
                    <span className="dato">{nomina.calificacion || "—"}</span>
                    <span className="tk-item-meta">{nomina.categoria || ""}</span>
                  </li>
                  <li>
                    <span className="et">Contrato</span>
                    {/* El período de prueba se pinta, y es el único de los tres
                        que se mira: vence, y hay que decidir antes. Son 24 hoy.
                        Por eso además sube a los avisos de arriba. */}
                    <span className={"pill " + (enPrueba ? "ojo" : "gris")}>
                      {nomina.contrato || "—"}
                    </span>
                  </li>
                  {nomina.obra_social && (
                    <li>
                      <span className="et">Obra social</span>
                      <span className="tk-item-meta">{nomina.obra_social}</span>
                    </li>
                  )}
                  {/* La baja se muestra sólo si la hay. Una línea «Motivo de
                      egreso: —» en la ficha de alguien que trabaja se lee como
                      si faltara un dato, cuando lo que pasa es que no se fue. */}
                  {!nomina.trabaja && (
                    <li>
                      <span className="et">Egreso</span>
                      <span className="pill mal">
                        {fecha(nomina.egreso) || "sin fecha"}
                      </span>
                      <span className="tk-item-meta">
                        {nomina.motivo_egreso || "sin motivo cargado"}
                      </span>
                    </li>
                  )}
                </ul>
              )}
            </BloqueFicha>
          )}

          {/* ── Cambios de empresa ─────────────────────────────────────────
              Sale de `personas.periodos`, que es donde ya estaba escrito: 4.679
              períodos cargados a mano, la mayoría firmados por Vilte. */}
          {verRrhh && (
            <BloqueFicha
              titulo="Cambios de empresa"
              ocultarSiVacio
              estado={
                periodos === undefined
                  ? "cargando"
                  : periodos === false
                    ? "error"
                    : "listo"
              }
              vacio={
                Array.isArray(periodos) && periodos.length === 0
                  ? "No tiene períodos cargados. Son 2.310 personas las que sí: no tenerlos quiere decir que a esta persona nunca se le anotó un ingreso o una baja, no que no los haya tenido."
                  : null
              }
            >
              {Array.isArray(periodos) && periodos.length > 0 && (
                <ul className="pf-papeles">
                  {periodos.map((p) => (
                    <li key={`${p.orden}-${p.inicio ?? "s"}`}>
                      <span className="et">
                        {p.empresa && p.empresa !== "0"
                          ? etiquetaCorta(p.empresa)
                          : "sin empresa"}
                      </span>
                      <span className={"pill " + (p.abierto ? "ok" : "gris")}>
                        {fecha(p.inicio) || "sin fecha"}
                        {p.abierto ? " · sigue" : ` → ${fecha(p.fin) || "?"}`}
                      </span>
                      <span className="tk-item-meta">
                        {p.detalle || "sin detalle"}
                        {p.cargado_por ? ` · lo anotó ${p.cargado_por}` : ""}
                        {/* NO SE CORRIGEN SOLAS: dar vuelta las fechas sería
                            inventar cuál de las dos está mal. Son 193 de 4.679
                            y se marcan para que quien las mire sepa que la
                            cuenta de esa fila no cierra. */}
                        {p.fechas_invertidas ? " · ⚠ fechas al revés" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </BloqueFicha>
          )}

          {/* ── Vacaciones · Partes de enfermo ─────────────────────────────
              Dos tarjetas y no una: son dos preguntas distintas —«¿cuándo se
              toma vacaciones?» y «¿hasta cuándo tiene certificado?»— y Igna
              las pidió como tarjetas separadas del historial. La fuente es la
              tabla `licencias`, la misma que dibuja la enfermería y la laguna
              del patio del bot; la cargan RRHH desde /rrhh/licencias o por
              WhatsApp. Cuelgan de `ver.rrhh`, como todo lo que dice quién
              trabaja y quién no. Sin ninguna cargada, la tarjeta no se dibuja:
              el texto de dónde se cargan sigue escrito para cuando vuelva a
              hacer falta. */}
          {verRrhh &&
            [
              ["vacaciones", "Vacaciones"],
              ["enfermo", "Partes de enfermo"],
            ].map(([tipoL, tituloL]) => {
              const propias = Array.isArray(licencias)
                ? licencias.filter((l) => l.tipo === tipoL)
                : null;
              return (
                <BloqueFicha
                  key={tipoL}
                  titulo={tituloL}
                  ocultarSiVacio
                  estado={
                    licencias === undefined
                      ? "cargando"
                      : licencias === false
                        ? "error"
                        : "listo"
                  }
                  vacio={
                    propias && propias.length === 0
                      ? tipoL === "vacaciones"
                        ? "Sin períodos de vacaciones cargados. Se cargan en Licencias (RRHH) o por WhatsApp con /licencia."
                        : "Sin partes de enfermo cargados. Se cargan en Licencias (RRHH) o por WhatsApp con /licencia."
                      : null
                  }
                  comoLlegar={
                    <Link className="rto-link" to="/rrhh/licencias">
                      Ir a Licencias
                    </Link>
                  }
                >
                  {propias && propias.length > 0 && (
                    <ul className="pf-papeles">
                      {propias.map((l) => {
                        const hoyIso = hoyAr();
                        const est = estadoDeLicencia(l, hoyIso);
                        const corre = est === "en_curso";
                        const altaVencida =
                          !l.anulado_en &&
                          tipoL === "enfermo" &&
                          l.hasta < hoyIso;
                        return (
                          <li key={l.id} className={l.anulado_en ? "tenue" : ""}>
                            <span className="et">
                              {fecha(l.desde)} → {fecha(l.hasta)}
                            </span>
                            <span
                              className={
                                "pill " +
                                (l.anulado_en
                                  ? "gris"
                                  : corre
                                    ? tipoL === "enfermo"
                                      ? "mal"
                                      : "ok"
                                    : altaVencida
                                      ? "ojo"
                                      : rotuloDeEstado(est, l, fecha).pill)
                              }
                            >
                              {/* «terminada» era el cajón donde caía TODO lo
                                  que no corre hoy, y ahí adentro estaba lo que
                                  todavía no empezó: unas vacaciones del 21/09
                                  se leían terminadas un 29/08. Cada estado se
                                  dice con su nombre. */}
                              {l.anulado_en
                                ? "anulada"
                                : corre
                                  ? tipoL === "enfermo"
                                    ? `con parte · alta el ${fecha(l.hasta)}`
                                    : "de vacaciones ahora"
                                  : altaVencida
                                    ? "alta vencida, sin novedad"
                                    : rotuloDeEstado(est, l, fecha).texto}
                            </span>
                            <span className="tk-item-meta">
                              {l.nota ? `${l.nota} · ` : ""}
                              {l.anulado_en
                                ? `la anuló ${l.anulado_por || "?"}`
                                : `lo cargó ${cargadaPor(l)}`}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </BloqueFicha>
              );
            })}

          {/* ── Novedades ──────────────────────────────────────────────────
              «La idea es que puedas consultar las novedades por empleado»
              (Nico, 25/08/2026). Ésta es esa pregunta contestada.

              ES EL HISTORIAL, y por eso incluye las licencias. Las dos
              tarjetas de arriba se pidieron «separadas del historial» y
              contestan preguntas puntuales —«¿cuándo se toma vacaciones?»,
              «¿hasta cuándo tiene certificado?»—. Ésta contesta la otra: qué
              le pasó a esta persona, todo junto y en orden. Las licencias NO
              se copian a `rrhh_novedades`: se leen de su tabla y se mezclan al
              dibujar, así nadie carga nada dos veces.

              Cuelga de `ver.rrhh` como todo lo que dice quién trabaja y quién
              no — y con más razón: acá hay suspensiones y partes médicos. */}
          {verRrhh &&
            (() => {
              const hay = Array.isArray(novedades);
              // ABSORBE LAS LICENCIAS QUE NO TIENEN TARJETA PROPIA, y sólo
              // ésas. Igna pidió una sola línea de tiempo «para no abrir dos
              // PANTALLAS»; en la ficha ya están las dos a la vista, así que
              // repetir acá un parte de enfermo que se ve veinte centímetros
              // más arriba no suma un dato: suma ruido, y encima invita a
              // anularlo desde el lugar equivocado.
              //
              // El filtro va por lo que TIENE tarjeta y no por lo que no la
              // tiene: si mañana aparece un tipo de licencia nuevo —gremial,
              // por estudio— cae acá solo, en vez de desaparecer de las dos
              // vistas sin que nadie se entere.
              const CON_TARJETA = ["vacaciones", "enfermo"];
              const sueltas = Array.isArray(licencias)
                ? licencias.filter((l) => !CON_TARJETA.includes(l.tipo))
                : [];
              const linea = hay ? lineaDeTiempo(novedades, sueltas) : null;
              return (
                <BloqueFicha
                  titulo="Novedades"
                  ocultarSiVacio
                  estado={
                    novedades === undefined
                      ? "cargando"
                      : novedades === false
                        ? "error"
                        : "listo"
                  }
                  vacio={
                    linea && linea.length === 0
                      ? "Sin novedades cargadas. Las suspensiones, fallecimientos y enfermedades sin parte se cargan en Novedades del personal (RRHH), con su documentación. Las vacaciones y los partes de enfermo tienen su propia tarjeta acá arriba."
                      : null
                  }
                  comoLlegar={
                    <Link className="rto-link" to="/rrhh/novedades">
                      Ir a Novedades del personal
                    </Link>
                  }
                >
                  {linea && linea.length > 0 && (
                    <ul className="pf-papeles">
                      {linea.map((n) => (
                        <li key={n.clave} className={n.anulado ? "tenue" : ""}>
                          <span className="et">
                            {fecha(n.fecha)}
                            {/* Sin `hasta` no se inventa una flecha a ningún
                                lado: un fallecimiento no termina. */}
                            {n.hasta ? ` → ${fecha(n.hasta)}` : ""}
                          </span>
                          <span
                            className={
                              "pill " + (n.anulado ? "gris" : "ojo")
                            }
                          >
                            {n.rotulo}
                          </span>
                          <span className="tk-item-meta">
                            {n.detalle ? `${n.detalle} · ` : ""}
                            {/* De dónde vino: una licencia se anula en
                                Licencias, y quien mire tiene que saber adónde
                                ir. */}
                            {n.origen === "licencia" ? "de Licencias · " : ""}
                            {n.anulado
                              ? "anulada"
                              : `lo cargó ${n.quien || "—"}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </BloqueFicha>
              );
            })()}
        </TemaFicha>

        {/* ── Papeles: documentación y lo legal ───────────────────────────── */}
        <TemaFicha id="papeles" titulo="Papeles" icono={<Icono nombre="carpeta" />}>
          {/* ── Documentación ──────────────────────────────────────────────
              Sólo para choferes (Igna, 02/10). La planilla de Marcos es de los
              que manejan; en la ficha de alguien de oficina «no está en la
              planilla» era una línea que no decía nada. Y si es chofer y no
              está, tampoco se dibuja acá: lo dice, más fuerte, el aviso de las
              dos altas. */}
          {verDocs && esChofer && (
            <BloqueFicha
              titulo="Documentación"
              ocultarSiVacio
              alerta={avisosDocs.length > 0}
              estado={
                docs === undefined
                  ? "cargando"
                  : docs === false
                    ? "error"
                    : "listo"
              }
              vacio={
                docs === null
                  ? "No está en la planilla de documentación. Son 224 choferes sobre 2.476 personas: no estar no es un dato que falte."
                  : null
              }
              comoLlegar={
                <Link className="rto-link" to="/documentacion">
                  Ir a Documentos
                </Link>
              }
            >
              {docs && (
                <>
                  {/* VA ARRIBA DE LOS PAPELES Y NO ABAJO. Los vencimientos se
                      siguen mostrando —están vencidos, y es verdad— pero leídos
                      sin esta línea mandan a llamar a alguien que ya no maneja.
                      Es la misma distinción que en la tabla: la baja no cambia
                      el veredicto sobre los papeles, cambia a quién hay que ir
                      a buscar. */}
                  {estaDeBaja(docs) && (
                    <p className="panel-aviso suave">
                      <b>Está de baja.</b> Sus vencimientos no cuentan en los
                      avisos ni en los números de Documentos. Se reactiva desde
                      esa pantalla.
                    </p>
                  )}
                  <ul className="pf-papeles">
                    {PAPELES.map(([campo, label, regla]) => {
                      /* Las peligrosas sólo para los marcados: sin este
                         filtro, 2 vencimientos reales se convierten en 53 y la
                         lista se deja de mirar. */
                      if (regla === "solo_marcados" && !r?.peligrosas)
                        return null;
                      const dias = diasHasta(docs[campo]);
                      const t = tono(dias);
                      return (
                        <li key={campo}>
                          <span className="et">{label}</span>
                          <span className={"pill " + t.clase}>{t.texto}</span>
                          <span className="tk-item-meta">
                            {fecha(docs[campo]) || "—"}
                            {/* MOSTRAR NO ES AVISAR: el psicofísico se ve y
                                no cuenta como faltante. Hay 142 vencidos de
                                153, y contarlos haría que la pantalla arranque
                                en rojo para casi todos. Se dice, para que
                                nadie crea que es un olvido. */}
                            {regla === "muestra" && " · no frena la salida"}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {docs.zona && (
                    <p className="tenue pf-chico">Zona: {docs.zona}</p>
                  )}
                  {/* Quién tocó la fila, con el mismo chip que el resto del
                      hub. La ficha es donde se mira un dato que parece mal, y
                      ahí es donde aparece la pregunta. */}
                  <p className="pf-chico">
                    <span className="et">Última corrección</span>{" "}
                    <ChipUsuario fila={docs} />
                  </p>
                </>
              )}
            </BloqueFicha>
          )}

          {/* ── Infracciones y siniestros: YA NO SE DIBUJA ─────────────────
              Era una tarjeta vacía a propósito, que decía por qué: no existe
              la tabla. Lo más parecido es `nocturnos`, que tiene 425 noches de
              circulación en horario prohibido pero está indexada POR INTERNO:
              dice que el 1523 anduvo de madrugada, no quién lo manejaba. Poner
              eso en el legajo de una persona sería una acusación sin respaldo,
              y en el legajo de la persona equivocada la mitad de las veces.

              Igna, 02/10: lo vacío no aparece. Esta tarjeta estaba vacía en
              TODAS las fichas, así que se fue entera. El día que haya de dónde
              sacarlo —siniestros cargados por chofer, o los nocturnos cruzados
              con portería para saber quién iba— vuelve, adentro de Papeles y
              sólo para choferes. */}

          {/* ── Multas de tránsito por DNI ─────────────────────────────────
              Actas reales de CABA/PBA/SINAI, la misma consulta que ya existe
              por dominio en la ficha de la unidad (`BloqueMultas`/`Unidad.jsx`),
              ahora por DNI.

              SÓLO PARA CHOFERES, y para ellos SIEMPRE, aunque no tenga actas:
              no es un dato guardado que pueda estar vacío sino una herramienta
              de consulta — el bloque es el botón. Esconderlo «porque no hay
              multas» sería esconder la única forma de averiguar si las hay. */}
          {verDocs && esChofer && (
            <BloqueFicha titulo="Multas de tránsito" estado="listo" ocultarSiVacio>
              <BloqueMultasPersona dni={limpio} puedeConsultar={puede("cargar.documentos")} />
            </BloqueFicha>
          )}

          {/* ── Las dos altas ──────────────────────────────────────────────
              Igna: «RRHH da de alta a X chofer pero no saben si tiene el
              registro al día; Marcos no lo da de alta pero mantiene la
              documentación». Este bloque es esa frase hecha pantalla, y era el
              único dato de la ficha que no estaba en ningún lado.

              VA AL FINAL DE PAPELES, Y SU AVISO ARRIBA DE TODO: cuando las dos
              altas se cruzaron —160 de 230— no dice nada que haga falta actuar,
              así que no puede ocupar el lugar de lo que sí. Cuando falta una,
              el aviso ya no depende de dónde esté el bloque: se nombra en la
              lista de «para mirar» de arriba y el bloque queda pintado.

              SÓLO PARA CHOFERES (Igna, 02/10): las dos altas son las de un
              chofer —RRHH y la planilla de Marcos—, y a una administrativa le
              «falta» la segunda porque no maneja. Si el cruce no se pudo leer
              no se sabe si es chofer, y entonces SÍ se dibuja, diciendo que no
              se pudo: callarlo sería tapar la falla con un vacío. */}
          {/* SIN `vacio`, Y NO ES UN OLVIDO: ese prop NO es «el texto cuando no
              hay datos» sino «este bloque no tiene contenido», y cuando está
              definido `BloqueFicha` dibuja el texto E IGNORA LOS CHILDREN. Con
              `vacio` puesto, este bloque mostraba siempre la misma línea y
              nunca el cruce. Lo agarró el test.

              El caso «no está en ninguno de los dos» lo cuenta `DosAltas` con
              sus propias dos líneas, que además dicen cuál falta. */}
          {/* PIDE `ver.rrhh`, y lo decidió un test que ya estaba.
              Escrito sin permiso, este bloque mostraba la calificación y el
              legajo de la nómina a cualquiera con `ver.gente` — o sea abría por
              la ventana lo que la puerta tiene con llave, que es exactamente lo
              que el encabezado de esta ficha dice que no hay que hacer.

              Se pierde algo real: quien mira sólo vencimientos no ve por qué a
              esta persona le faltan. A cambio no se filtra la nómina, y ése es
              el lado correcto de la balanza — el dato de RRHH es de RRHH aunque
              se muestre en una pantalla de documentación. */}
          {verRrhh && (esChofer || cruce.estado === "error") && (
            <BloqueFicha
              titulo="Las dos altas"
              estado={cruce.estado}
              ocultarSiVacio
              alerta={alertaAltas}
            >
              {cruce.dato && <DosAltas cruce={cruce.dato} />}
            </BloqueFicha>
          )}
        </TemaFicha>
      </div>
    </div>
  );
};

export default Persona;
